import React from 'react';

interface CardProps {
  variant?: 'elevated' | 'outlined' | 'filled';
  padding?: 'none' | 'sm' | 'md' | 'lg';
  children: React.ReactNode;
  className?: string;
  interactive?: boolean;
  onClick?: () => void;
}

/**
 * Design System Card Component
 * 
 * Variants:
 * - Elevated: Subtle shadow, floating appearance
 * - Outlined: Border-based, clean separation
 * - Filled: Background-based, subtle contrast
 */
export const Card: React.FC<CardProps> = ({
  variant = 'elevated',
  padding = 'md',
  children,
  className = '',
  interactive = false,
  onClick,
}) => {
  const baseStyles = `
    rounded-xl transition-all duration-200 ease-out
    ${interactive ? 'cursor-pointer' : ''}
  `;

  const variantStyles = {
    elevated: `
      bg-seasalt shadow-sm border border-anti-flash-white
      ${interactive ? 'hover:shadow-md hover:border-platinum hover:-translate-y-0.5' : ''}
    `,
    outlined: `
      bg-transparent border border-border-medium
      ${interactive ? 'hover:border-border-dark hover:bg-surface-secondary' : ''}
    `,
    filled: `
      bg-surface-secondary border border-transparent
      ${interactive ? 'hover:bg-platinum hover:border-border-light' : ''}
    `,
  };

  const paddingStyles = {
    none: '',
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8',
  };

  const combinedClassName = `
    ${baseStyles}
    ${variantStyles[variant]}
    ${paddingStyles[padding]}
    ${className}
  `.trim();

  return (
    <div className={combinedClassName} onClick={onClick}>
      {children}
    </div>
  );
};

interface CardHeaderProps {
  children: React.ReactNode;
  className?: string;
}

export const CardHeader: React.FC<CardHeaderProps> = ({ children, className = '' }) => (
  <div className={`mb-4 ${className}`}>
    {children}
  </div>
);

interface CardContentProps {
  children: React.ReactNode;
  className?: string;
}

export const CardContent: React.FC<CardContentProps> = ({ children, className = '' }) => (
  <div className={`${className}`}>
    {children}
  </div>
);

interface CardFooterProps {
  children: React.ReactNode;
  className?: string;
}

export const CardFooter: React.FC<CardFooterProps> = ({ children, className = '' }) => (
  <div className={`mt-4 pt-4 border-t border-border-light ${className}`}>
    {children}
  </div>
);